/**
 * The host's tour of the study room (2026-10-05, the user's request): nine stops (ten until the entrance board went, 2026-10-07) from the
 * reception round the room and back, each with a very short line. Pure, so the stops are
 * tested against the room plan: every viewpoint is on open floor, looks at what it talks
 * about, and Luma can walk from each of her spots to the next (`navigation.ts`).
 *
 * Luma walks to each stop on her own; when she arrives the visitor is moved to the viewpoint
 * beside her, she points and says the line, and walks on to the next by herself. The same
 * places are described for questions in `server/src/room-guide.ts`.
 */
import { WARD } from '../ward/layout.ts';
import {
  ARC,
  CENTRE,
  HALL,
  PRESENTATION,
  RECEPTION,
  STUDIO,
  STUDIO_FITTINGS,
  TABLE,
  arcPlan,
  aroundArc,
  receptionPoint,
} from '../world/plan.ts';

export const HOST_NAME = 'Luma';

export interface TourStop {
  id: string;
  title: string;
  /** What the host says (also shown on the host's card). */
  line: string;
  /** Where the visitor stands, and the point they face. Absent: stay where they are. */
  view?: { x: number; z: number; look: [number, number, number] };
  /** Where Luma stands, when the rule in `hostSpotFor` would put her in the furniture. */
  host?: { x: number; z: number };
  /** What the host points at. */
  point: [number, number, number];
}

export interface TourStudy {
  code: string;
  /** People the host can greet by name. */
  visitor?: string;
}

export function tourStops(study: TourStudy): TourStop[] {
  const plan = arcPlan();
  const centre = aroundArc(CENTRE.angle, ARC.radius - CENTRE.inset);
  const leftArc = aroundArc(plan.angles[2]!, ARC.radius);
  const host = receptionPoint(RECEPTION.host.x, RECEPTION.host.z);
  const atReception = receptionPoint(0.1, 1.25);
  const wb = { x: STUDIO.x0, z: STUDIO_FITTINGS.boardZ };
  const shelf = STUDIO_FITTINGS.shelf;
  const display = { x: PRESENTATION.x, y: PRESENTATION.y, z: HALL.front };
  return [
    {
      id: 'reception',
      title: 'The reception',
      // No introduction: she has already welcomed the visitor by name (2026-10-06).
      line: `We start here at the reception. Whenever you need me, ask about ${study.code} or this room, by voice or by typing.`,
      point: [host.x, 1.6, host.z],
    },
    {
      id: 'console',
      title: 'The room console',
      line: `The room console filters every screen, and its thumbnails put any dashboard on the centre screen.`,
      view: { x: TABLE.x + 0.35, z: TABLE.z + 2.1, look: [TABLE.x, 0.9, TABLE.z + 0.6] },
      host: { x: TABLE.x + 1.3, z: TABLE.z + 1.45 },
      point: [TABLE.x, 1.05, TABLE.z + 0.66],
    },
    {
      id: 'centre',
      title: 'The centre screen',
      line: `The centre screen opens on the study summary. A dashboard shown here gets its 3D chart on the floor in front of it, and a miniature on the table.`,
      view: { x: TABLE.x + 0.35, z: TABLE.z + 2.1, look: [centre.x, CENTRE.centreY, centre.z] },
      host: { x: TABLE.x + 1.3, z: TABLE.z + 1.45 },
      point: [centre.x, CENTRE.centreY, centre.z],
    },
    {
      id: 'arc',
      title: 'The dashboard wall',
      line: `Eleven dashboards are set into the curved wall. Point at their 3D stages for the numbers, drill down to the records, and raise queries.`,
      view: { x: TABLE.x - 3.4, z: TABLE.z - 1.6, look: [leftArc.x, 1.6, leftArc.z] },
      point: [leftArc.x, 1.85, leftArc.z],
    },
    {
      id: 'whiteboard',
      title: 'The collaboration studio',
      line: `This is the collaboration studio. The shared whiteboard has pens and sticky notes on its tray, and snapshots you can keep.`,
      view: { x: wb.x + 3.2, z: wb.z + 0.8, look: [wb.x, 1.5, wb.z] },
      point: [wb.x, 1.6, wb.z],
    },
    {
      id: 'shelf',
      title: 'The model shelf',
      line: `The model shelf: the anatomy, the study drug's molecule, the site map and the dosing timeline, ready for the table.`,
      view: { x: shelf.x + 0.3, z: shelf.z + 2.0, look: [shelf.x, 1.2, shelf.z] },
      host: { x: shelf.x + 1.6, z: shelf.z + 1.2 },
      point: [shelf.x, 1.3, shelf.z],
    },
    {
      id: 'presentation',
      title: 'Presentation display and lectern',
      line: `The presentation display and lectern, for slides of any dashboard, or a screen shared from a desktop.`,
      view: { x: display.x + 0.6, z: display.z - 4.4, look: [display.x, display.y, display.z] },
      point: [display.x, display.y, display.z],
    },
    {
      id: 'ward',
      title: 'The patient ward',
      line: `Every subject stands in the patient ward, and the rows run on into the ward room. Select one for their profile, or hold the grip to pull them out for review.`,
      view: {
        x: WARD.entrance.x,
        z: WARD.entrance.z,
        look: [WARD.entrance.x + 3.5, 1.2, WARD.entrance.z],
      },
      point: [WARD.entrance.x + 3.0, 1.2, WARD.entrance.z],
    },
    {
      id: 'wrist',
      title: 'Your wrist menu, and me',
      line: `Your wrist menu has teleports, mute and more. That's the tour. I'm at the reception whenever you need me.`,
      view: { x: atReception.x, z: atReception.z, look: [host.x, 1.5, host.z] },
      // Back at her post behind the counter.
      host: { x: host.x, z: host.z },
      point: [host.x, 1.5, host.z],
    },
  ];
}

/** Where the host stands at a stop: ahead of the visitor and to their right, facing them. */
export function hostSpotFor(stop: TourStop): { x: number; z: number; yaw: number } | undefined {
  if (!stop.view) return undefined;
  const v = stop.view;
  if (stop.host) return { ...stop.host, yaw: Math.atan2(v.x - stop.host.x, v.z - stop.host.z) };
  let dx = v.look[0] - v.x;
  let dz = v.look[2] - v.z;
  const len = Math.hypot(dx, dz) || 1;
  dx /= len;
  dz /= len;
  // Facing (dx, dz), the visitor's right is (-dz, dx).
  const x = v.x + dx * 1.3 - dz * 0.85;
  const z = v.z + dz * 1.3 + dx * 0.85;
  return { x, z, yaw: Math.atan2(v.x - x, v.z - z) };
}

/** What a visitor says that the host acts on without asking the assistant. */
export type HostIntent = 'tour' | 'next' | 'back' | 'repeat' | 'stop' | undefined;

export function intentOf(text: string): HostIntent {
  const t = text
    .toLowerCase()
    .replace(/[^a-z' ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!t) return undefined;
  if (/\b(tour|show me around|show me the room|look around|guide me|walk me through)\b/.test(t))
    return 'tour';
  if (/^(next|next one|go on|carry on|continue|ok next|okay next)$/.test(t)) return 'next';
  if (/^(back|go back|previous)$/.test(t)) return 'back';
  if (/^(repeat|say that again|again|pardon|sorry)$/.test(t)) return 'repeat';
  if (
    /^(stop|cancel|that's all|that is all|no thanks|no thank you|thanks|thank you|thank you very much|thanks luma|thank you luma|bye|goodbye|that's it)$/.test(
      t,
    )
  )
    return 'stop';
  return undefined;
}

/** The welcome as the visitor enters. */
export function welcomeLine(study: TourStudy): string {
  const who = study.visitor ? `, ${study.visitor}` : '';
  return `Welcome to ${study.code}${who}. I'm ${HOST_NAME}, your host. Would you like me to show you around the room, or is there something about the study I can help with?`;
}
